// Paste-as-comma-list helpers. Turn newline-delimited text (the clipboard) into a
// single comma-separated line, optionally quoting each value. Used by the editor
// keyboard shortcuts (CodeMirrorSQL wrapper → every editor) + the query-editor
// right-click menu.
//
// Pure `toCommaList` is unit-tested (pastecomma.test.mjs); the view/clipboard
// helpers import toast LAZILY so the test can import this module under plain node
// (no top-level solid/CodeMirror imports).

// toCommaList: split on newlines, trim each line, drop blanks, join with ", ".
// quote: '' (none) | "'" | '"'. When quoting, an embedded quote char is doubled so
// the output stays a valid SQL string / identifier literal (O'Brien → 'O''Brien').
export function toCommaList(text, quote = '') {
  const lines = String(text ?? '')
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (!quote) return lines.join(', ');
  const esc = (s) => s.split(quote).join(quote + quote);
  return lines.map((s) => quote + esc(s) + quote).join(', ');
}

const notify = async (kind, msg) => {
  try { const { toast } = await import('./toast.js'); toast[kind](msg); } catch { /* node/no-ui */ }
};

// pasteCommaIntoView reads the clipboard, transforms it (toCommaList), and replaces
// the current selection in a CodeMirror view. quote as in toCommaList.
export async function pasteCommaIntoView(view, quote = '') {
  if (!view) return;
  let raw = '';
  try {
    if (navigator.clipboard?.readText) raw = await navigator.clipboard.readText();
  } catch { /* insecure context / denied permission */ }
  if (!raw.trim()) {
    notify('error', 'Clipboard is empty or unreadable (needs HTTPS or localhost).');
    return;
  }
  const out = toCommaList(raw, quote);
  if (!out) { notify('error', 'Nothing to paste.'); return; }
  view.dispatch(view.state.replaceSelection(out));
  view.focus();
}

// commaPasteMenuItems returns the three context-menu entries (plain + both quoted)
// for a host editor's right-click menu.
export function commaPasteMenuItems(view) {
  return [
    { label: '⬇ Paste comma list',            onClick: () => pasteCommaIntoView(view, '') },
    { label: "⬇ Paste comma list — 'quoted'", onClick: () => pasteCommaIntoView(view, "'") },
    { label: '⬇ Paste comma list — "quoted"', onClick: () => pasteCommaIntoView(view, '"') },
  ];
}
